import { MoveListComponent } from 'components/move-list/config/types';
import { defaultTranslations } from 'chessboard/model/move-list/translations';
import MoveList from 'components/move-list/move-list.svelte';
import { createMoveListStateMachine } from 'components/move-list/state-machine';
import { Actions } from 'components/move-list/state-machine/types';
import createStore from 'components/move-list/store';
import createEventHandlers from 'components/move-list/state-machine/events';
import 'components/move-list/move-list-component.scss';
import { createSvelteComponent } from 'src/utils/svelte/create-svelte-component';
import type { Game } from 'chessboard/model/game/types';
import { createQueue } from 'src/utils/queue';
import { Board } from 'chessboard/model/board/types';
import { createContextMenuActions } from './components/context-menu/actions';
import { getMoveNodeEl } from './helpers/dom/get-move-node-el';
import { isMatchingNode } from './helpers/is-matching-node';

/**
 * Create move list component
 */
export function createMoveList({
  target,
  translations = defaultTranslations,
  game,
  hideTimestampText,
  displayTimeInClockFormat = true,
  showTimestamps,
  scrollContainer,
  customColorsEnabled = false,
}: MoveListComponent.Params): MoveListComponent.API {
  const { data, actions } = createStore(game, {
    baseTime: game.timeControl.get()?.baseTime,
    textType: game.getOptions().moveListDisplayType,
    classificationStyle:
      game.getOptions().moveListClassificationStyle ?? Board.MoveListClassificationStyles.Default,
    menuEnabled: game.getOptions().moveListContextMenuEnabled,
    moveListEl: target,
    scrollContainer,
    translations,
    customColorsEnabled,
    displayTimeInClockFormat,
  });

  const queue = createQueue(false);

  const contextMenuActions = createContextMenuActions(data, actions);
  actions.addContextMenuActions(Object.values(contextMenuActions));

  const moveListService = createMoveListStateMachine({ data, actions, game });

  const eventHandlers = createEventHandlers(moveListService.send, { data, game, actions, queue });

  const component = createSvelteComponent({
    ComponentType: MoveList,
    options: {
      props: {
        data,
        eventHandlers,
        hideTimestampText,
        showTimestamps,
        game,
      },
      target,
    },
    replaceContainer: false,
  });

  // class needed for integration
  if (!target.classList.contains('move-list')) {
    target.classList.add('move-list');
  }

  // class needed for component styling in this repo
  if (!target.classList.contains('chessboard-pkg-move-list-component')) {
    target.classList.add('chessboard-pkg-move-list-component');
  }

  return {
    destroy,
    setTextType: actions.setTextType,
    setContextMenuEnabled: (enabled: boolean) => actions.setContextMenu({ enabled }),
    setBaseTime: (baseTime: number | undefined) => {
      actions.setBaseTime(baseTime);
    },
    setResult: (result: Game.Results | null) => {
      actions.setResult(result);
      actions.setGameMetadataFromGameInfo(game);
    },
    setActiveStatus: (status: boolean) => {
      actions.setActiveStatus(status);
    },
    isActive: () => data.isActive.get(),
    selectMainLineStart() {
      moveListService.send(Actions.DESELECT_NODE);
      (scrollContainer ?? target).scrollTop = 0;
    },
    selectNode: (node) => {
      moveListService.send(Actions.SELECT_NODE, { node });
    },
    loadLine: (lines, result, node) => {
      moveListService.send(Actions.LOAD_LINE, { lines, result, node });
    },
    addMainLineNode: (node, selectedNode) => {
      if (!node) {
        return;
      }

      actions.addMainLineNode(node);
      actions.setContextMenu({ isOpen: false });
      actions.setVariationPopover({ isOpen: false });

      if (!isMatchingNode(selectedNode, data.selectedNodeKeys.get())) {
        moveListService.send(Actions.SELECT_NODE, {
          node: selectedNode,
          disableScrollIfScrolledUp: !node.userGenerated,
        });
      }
    },
    deselectNode: () => moveListService.send(Actions.DESELECT_NODE),
    addContextMenuActions: actions.addContextMenuActions,
    removeContextMenuActions: actions.removeContextMenuActions,
    setAnalysisHidden: (hidden: boolean) => {
      setAnalysisFeatureVisibility({
        hideAnnotations: hidden,
        hideComments: hidden,
        variationVisibility: hidden
          ? MoveListComponent.VariationVisibility.None
          : MoveListComponent.VariationVisibility.All,
      });
    },
    setAnnotationsHidden: (hidden: boolean) => {
      setAnalysisFeatureVisibility({ hideAnnotations: hidden });
    },
    setVariationsHidden(hidden: boolean) {
      setAnalysisFeatureVisibility({
        variationVisibility: hidden
          ? MoveListComponent.VariationVisibility.None
          : MoveListComponent.VariationVisibility.All,
      });
    },
    setVariationVisibility(visibility: MoveListComponent.VariationVisibility) {
      setAnalysisFeatureVisibility({ variationVisibility: visibility });
    },
    getVariationVisibility: () => data.variationVisibility.get(),
    setCommentsHidden: (hidden: boolean) => {
      setAnalysisFeatureVisibility({ hideComments: hidden });
    },
    setCommentsEditable: actions.setCommentsEditable,
    getMoveNodeEl: (line: number, move: number) => getMoveNodeEl(target, line, move),
    openVariationsPopover: eventHandlers.openVariationsPopover,
    closeVariationsPopover: eventHandlers.closeVariationsPopover,
    enableNodeLimits: actions.enableNodeLimits,
    updateNodeLimits: actions.setNodeLimits,
    getNodeLimits: () => {
      const getNode = (ids) => game.getLine(ids?.line)?.[ids?.move] || null;
      const begin = getNode(data.nodeLimits.get().begin);
      const focus = getNode(data.nodeLimits.get().focus);
      const end = getNode(data.nodeLimits.get().end);

      return { begin, focus, end };
    },
    setCustomColorsEnabled: actions.setCustomColorsEnabled,
    reinitialize: () => {
      data.finishedInitialLoad.set(false);
    },
    getQueue: () => queue,
    refreshNode: actions.refreshNode,
    setClassificationStyle: actions.setClassificationStyle,
    setNodeEffectOverrides: actions.setNodeEffectOverrides,
    getNodeEffectOverrides: () => data.nodeEffectOverrides.get(),
  };

  /**
   * Set analysis features as hidden or visible
   */
  function setAnalysisFeatureVisibility(options: {
    hideComments?: boolean;
    variationVisibility?: MoveListComponent.VariationVisibility;
    hideAnnotations?: boolean;
  }): void {
    const { hideComments, variationVisibility, hideAnnotations } = options;

    if (hideComments !== undefined) {
      actions.setCommentsHidden(hideComments);
    }
    if (variationVisibility !== undefined) {
      actions.setVariationVisibility(variationVisibility);
    }
    if (hideAnnotations !== undefined) {
      actions.setAnnotationsHidden(hideAnnotations);
    }

    if (hideComments !== undefined || variationVisibility !== undefined) {
      // need to reload the line to strip out comments and variations that split the main line
      moveListService.send(Actions.LOAD_LINE, {
        lines: game.getRawLines(),
        result: data.result.get(),
        node: game.getSelectedNode(),
      });
    }
  }

  /**
   * destroy the move list
   */
  function destroy() {
    component.destroy();
    queue.clear();
  }
}
